import React, { useEffect, useRef } from "react";
import { Autocomplete, styled as muiStyled, Paper } from "@mui/material";
import styled from "styled-components";
import { colors, fonts } from "../../components/common/mixins";
import { DsTextField } from "./DsTextField";
import { ArrowDown, Clear } from "../../components/icons";
import { BREAKPOINT_RESERVATION_PAGE } from "../../pageComponents/reservationPage/constants/breakpoints";

const StyledAutocomplete = muiStyled(Autocomplete)`
  & .MuiOutlinedInput-root .MuiAutocomplete-endAdornment {
    right: 12px;
  }

  // Space between Arrow button and Label
  & .MuiInputLabel-outlined:not(.MuiInputLabel-shrink) {
    max-width: calc(100% - 52px);
  }

  & .MuiAutocomplete-popupIndicator {
    transition: transform 0.2s ease;
  }

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    & .MuiInputBase-input {
      font-size: 16px !important; /* prevents iOS auto-zoom on focus */
    }
  }
`;

const StyledListbox = styled.ul`
  && {
    padding: 12px;
    max-height: 33vh;
  }

  display: grid;
  grid-auto-flow: row;
  gap: 8px;

  ${fonts.normalText()}

  && .MuiAutocomplete-option {
    border-radius: 4px;
    transition: background-color 0.15s ease;

    /* default: no sticky selected background */
    &[aria-selected="true"],
    &[aria-selected="true"].Mui-focused {
      background-color: transparent;
    }

    /* highlight on hover / keyboard navigation */
    &:hover,
    &.Mui-focused,
    &[aria-selected="true"]:hover {
      background-color: ${colors.menuItemHovered};
    }
`;

const DropdownPaper = muiStyled(Paper)`
  border: 1px solid ${colors.formDefault};
  border-radius: 8px;
  margin-top: 8px;
  overflow: hidden; /* scroll bar hidden */
`;

export function DsAutocomplete<T>(
  props: React.ComponentProps<typeof Autocomplete<T, false, false, false>> & {
    autoSelectSingleOption: boolean;
  },
) {
  const { renderInput, autoSelectSingleOption, disabled: disabledProp, ...rest } = props;

  const autoSelectedLabelRef = useRef<string | null>(null);
  const onChangeRef = useRef(rest.onChange);
  onChangeRef.current = rest.onChange;
  const getOptionLabelRef = useRef(rest.getOptionLabel);
  getOptionLabelRef.current = rest.getOptionLabel;

  useEffect(() => {
    if (!autoSelectSingleOption) return;
    if (rest.options.length !== 1) {
      autoSelectedLabelRef.current = null;
      return;
    }
    const singleOption = rest.options[0] as T;
    const label = getOptionLabelRef.current?.(singleOption) ?? String(singleOption);
    if (autoSelectedLabelRef.current === label) return;
    if (rest.value != null) return;
    autoSelectedLabelRef.current = label;
    onChangeRef.current?.(null as any, singleOption, "selectOption");
  }, [autoSelectSingleOption, rest.options, rest.value]);

  const disabled = disabledProp || !!(autoSelectSingleOption && rest.options.length === 1 && rest.value != null);
  const hasValue =
    rest.value != null && (!Array.isArray(rest.value) || rest.value.length > 0);

  return (
    <StyledAutocomplete
      {...rest}
      disabled={disabled}
      forcePopupIcon={!disabled && !hasValue}
      popupIcon={!disabled ? <ArrowDown /> : null}
      clearIcon={!disabled ? <Clear /> : null}
      renderInput={(params) => {
        if (renderInput) return renderInput(params);

        return (
          <DsTextField
            {...params}
            inputProps={{
              ...params.inputProps,
              autoComplete: "autocomplete-off",
            }}
          />
        );
      }}
      ListboxComponent={StyledListbox as any}
      PaperComponent={(paperProps) => (
        <DropdownPaper {...paperProps}>{paperProps.children}</DropdownPaper>
      )}
    />
  );
}
